import { useParams, useSearchParams } from 'react-router-dom'

import { useGetConfig } from '@jotta/grpc-connect-client/config'
import { Feature } from '@jotta/grpc-connect-openapi/feature'
import { RecurringSubscriptionInterval } from '@jotta/grpc-connect-openapi/payment'
import { isLocale, useActivateLocale } from '@jotta/i18n'
import { useAndConsumeSearchParam } from '@jotta/router'
import { LoadingOverlay } from '@jotta/ui/LoadingOverlay'
import { Redirect } from '@jotta/ui/Redirect'

import { BusinessSignup } from './BusinessSignup'
import { FreeSignup } from './FreeSignup'
import { PaidSignup } from './PaidSignup'
import { VoucherSignup } from './VoucherSignup'

function Signup({
  productCode,
  voucherCode,
  defaultSubscriptionInterval,
}: {
  productCode?: string
  voucherCode?: string
  defaultSubscriptionInterval: RecurringSubscriptionInterval
}) {
  const { data: config } = useGetConfig()

  if (config) {
    const activeFeaturesList = config?.activeFeatures
    const hasSignup = activeFeaturesList?.includes(Feature.SIGNUP)
    const hasVoucherSignup = activeFeaturesList?.includes(
      Feature.VOUCHER_SIGNUP,
    )
    const hasPreSignup = activeFeaturesList?.includes(Feature.PRE_SIGNUP)

    if (hasSignup) {
      if (productCode) {
        switch (productCode) {
          case 'voucher':
            return <VoucherSignup voucherCode={voucherCode} />
          case 'business':
            return <BusinessSignup />
          case 'free':
            return <FreeSignup allowAppPromotion />
          default:
            return (
              <PaidSignup
                productCode={productCode}
                defaultSubscriptionInterval={defaultSubscriptionInterval}
              />
            )
        }
      } else {
        return <FreeSignup allowAppPromotion />
      }
    } else if (hasVoucherSignup) {
      return <VoucherSignup voucherCode={voucherCode} />
    } else if (hasPreSignup) {
      return <Redirect to="/api/login" reload />
    } else {
      window.location.href = '/'
    }
  }

  return <LoadingOverlay id="SignupRoute" open />
}

const productCodeMap: Record<string, string> = {
  personal: 'private_personal',
  unlimited: 'private_personal',
  business_small: 'business_small_v2020',
  business_medium: 'business_medium_v2020',
  business_large: 'business_large_v2020',
}

export function SignupRoute() {
  const { productCode: productCodeParam } = useParams()
  const voucherCode = useAndConsumeSearchParam('voucher_code')
  const lang = useAndConsumeSearchParam('lang') // propagated from cms
  const [searchParams] = useSearchParams()

  useActivateLocale({
    overrideLocale: isLocale(lang) ? lang : undefined,
  })

  const subscriptionInterval =
    searchParams.get('interval') === 'monthly'
      ? RecurringSubscriptionInterval.MONTHLY_RECURRING
      : RecurringSubscriptionInterval.YEARLY_RECURRING

  const productCode = productCodeParam
    ? productCodeMap[productCodeParam] || productCodeParam
    : undefined

  return (
    <Signup
      productCode={productCode}
      voucherCode={voucherCode}
      defaultSubscriptionInterval={subscriptionInterval}
    />
  )
}
